


import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatDistanceToNow } from "date-fns";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Plus, FileText, BarChart3, Users, Eye } from "lucide-react";
import Sidebar from "../Layout/sidebar";

const DEMO_USER_ID = "demo-user-1";

export default function FormDashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: [`/api/dashboard/stats?userId=${DEMO_USER_ID}`],
  });

  const { data: forms, isLoading: formsLoading } = useQuery({
    queryKey: [`/api/forms?userId=${DEMO_USER_ID}`],
  });

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "grey.50" }} data-testid="dashboard-page">
      <Sidebar />

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: "white",
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" fontWeight="600" color="text.primary" data-testid="page-title">
              Dashboard
            </Typography>
            <Chip
              label={stats?.mongodbConnected ? "MongoDB Connected" : "In-Memory Storage"}
              size="small"
              sx={{
                bgcolor: stats?.mongodbConnected ? "success.light" : "warning.light",
                color: stats?.mongodbConnected ? "success.dark" : "warning.dark",
                fontWeight: 500,
              }}
            />
          </Box>
          <Link href="/forms/new">
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              sx={{ bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
              data-testid="button-create-form"
            >
              Create Form
            </Button>
          </Link>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 3 }}>
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {[
              { title: "Total Forms", icon: <FileText size={18} />, value: stats?.totalForms || 0 },
              { title: "Published Forms", icon: <Eye size={18} />, value: stats?.publishedForms || 0 },
              { title: "Total Responses", icon: <BarChart3 size={18} />, value: stats?.totalResponses || 0 },
              { title: "Storage Type", icon: <Users size={18} />, value: stats?.mongodbConnected ? "MongoDB" : "Memory" },
            ].map((item, idx) => (
              <Grid item xs={12} md={6} lg={3} key={idx}>
                <Card>
                  <CardHeader
                    title={
                      <Typography variant="subtitle2" color="text.secondary">
                        {item.title}
                      </Typography>
                    }
                    action={<Box color="text.secondary">{item.icon}</Box>}
                    sx={{ pb: 0 }}
                  />
                  <CardContent>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      data-testid={`stat-${item.title.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {statsLoading ? "..." : item.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Forms Table */}
          <Paper>
            <Box p={2}>
              <Typography variant="h6">Recent Forms</Typography>
            </Box>
            <Box p={2}>
              {formsLoading ? (
                <Box textAlign="center" py={4} data-testid="loading-forms">
                  <CircularProgress color="primary" />
                  <Typography color="text.secondary" mt={2}>
                    Loading forms...
                  </Typography>
                </Box>
              ) : !forms || forms.length === 0 ? (
                <Box textAlign="center" py={4} data-testid="empty-forms">
                  <FileText size={48} style={{ color: "#9ca3af", marginBottom: 16 }} />
                  <Typography color="text.secondary" mb={2}>
                    No forms created yet
                  </Typography>
                  <Link href="/forms/new">
                    <Button variant="contained" data-testid="button-create-first-form">
                      Create your first form
                    </Button>
                  </Link>
                </Box>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table data-testid="forms-table">
                    <TableHead>
                      <TableRow>
                        {["Form Name", "Status", "Responses", "Last Response", "Actions"].map((head) => (
                          <TableCell key={head} sx={{ fontWeight: "bold" }}>
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {forms.map((form) => (
                        <TableRow
                          key={form._id}
                          hover
                          data-testid={`form-row-${form._id}`}
                          sx={{ "&:last-child td": { border: 0 } }}
                        >
                          <TableCell>
                            <Typography fontWeight={500} data-testid={`form-title-${form._id}`}>
                              {form.title}
                            </Typography>
                            {form.description && (
                              <Typography variant="body2" color="text.secondary" data-testid={`form-description-${form._id}`}>
                                {form.description}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={form.status}
                              size="small"
                              sx={{
                                bgcolor:
                                  form.status === "published"
                                    ? "success.light"
                                    : form.status === "archived"
                                    ? "grey.200"
                                    : "warning.light",
                                color:
                                  form.status === "published"
                                    ? "success.dark"
                                    : form.status === "archived"
                                    ? "text.primary"
                                    : "warning.dark",
                                fontWeight: 500,
                              }}
                              data-testid={`form-status-${form._id}`}
                            />
                          </TableCell>
                          <TableCell data-testid={`form-responses-${form._id}`}>{form.responseCount}</TableCell>
                          <TableCell data-testid={`form-last-response-${form._id}`}>
                            {form.lastResponse
                              ? formatDistanceToNow(new Date(form.lastResponse), { addSuffix: true })
                              : "Never"}
                          </TableCell>
                          <TableCell>
                            <Link href={`/forms/${form._id}`}>
                              <Button variant="outlined" size="small" data-testid={`button-edit-${form._id}`}>
                                Edit
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}
